import { yupResolver } from '@hookform/resolvers/yup'
import { FC, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { useUser } from '@/api/models/common'
import { PaymentCard } from '@/api/models/finance'
import { ScreenSize } from '@/app/constants'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import { useCurrentBreakpoint } from '@/app/hooks/useCurrentBreakpoint'
import { ErrorCard } from '@/components/pages/plugs'
import { AppButton } from '@/components/shared/button'
import GoBackButton from '@/components/shared/button/ui/GoBackButton'
import CardsGroup from '@/components/shared/cards-group/ui/CardsGroup'
import { AppDrawer } from '@/components/shared/drawer'
import { ButtonSkeleton, CardSkeleton } from '@/components/shared/skeleton'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'
import { MODAL_TITLE_ERROR } from '@/utils/constants'
import { formatNumberLocale } from '@/utils/number'

import { BillsListItem } from './BillsListItem/BillsListItem'
import AcquiringPayForm from './BillsPayDrawer/AcquiringPayForm'
import { receiptMethodsOptions } from './BillsPayDrawer/BillsPayDrawer.constants'
import {
  BalanceInvoiceProp,
  PayMethods
} from './BillsPayDrawer/BillsPayDrawer.types'
import {
  getBalanceInvoice,
  getBalanceInvoiceId
} from './BillsPayDrawer/BillsPayDrawer.utils'
import {
  BillsPayFooterContent,
  BillsPayFooterWrapper
} from './BillsPayDrawer/BillsPayFooter'
import BillsPayInformation from './BillsPayDrawer/BillsPayInformation'
import { useBillsFinancePay } from './BillsPayDrawer/hooks/useBillsFinancePay'
import { useBillsPaymentMethods } from './BillsPayDrawer/hooks/useBillsPaymentMethods'
import {
  AcquiringFormData,
  acquiringSchema
} from './BillsPayDrawer/model/schemas'

type Props = {
  invoice: BalanceInvoiceProp
  invoiceIds: string[]
  opened?: boolean
  isCommonPayment?: boolean
  onClose: () => void
  setInvoiceListDrawerOpened?: (open: boolean) => void
}

const BillsPayDrawer: FC<Props> = ({
  invoice,
  invoiceIds,
  opened,
  isCommonPayment,
  onClose,
  setInvoiceListDrawerOpened
}) => {
  const { breakpoint } = useCurrentBreakpoint()
  const isMobileScreen = [
    ScreenSize.Xxs,
    ScreenSize.Xs,
    ScreenSize.Sm
  ].includes(breakpoint)

  const {
    paymentMethods,
    payMethodRadioOptions,
    isError: isPaymentMethodsError,
    isLoading: isPaymentMethodsLoading
  } = useBillsPaymentMethods({
    invoiceId: getBalanceInvoiceId(invoice, invoiceIds),
    isCommonPayment
  })

  const balanceInvoice = useMemo(
    () => getBalanceInvoice(invoice, paymentMethods),
    [invoice, paymentMethods]
  )

  const { data: user } = useUser()

  const [selectedPayMethod, setSelectedPayMethod] = useState<
    PaymentCard | undefined
  >(undefined)

  const [isPayRequestRejected, setIsPayRequestRejected] = useState(false)
  const isError = isPaymentMethodsError || isPayRequestRejected

  const acquiringForm = useForm<AcquiringFormData>({
    defaultValues: {
      amount: String(formatNumberLocale(balanceInvoice.debtSum)) || undefined,
      phone: user?.phone_number ?? '',
      email: user?.email ?? ''
    },
    resolver: yupResolver(acquiringSchema),
    context: { selectedReceiptMethod: receiptMethodsOptions[0] },
    mode: 'onSubmit'
  })

  const { reachGoal } = useYandexMetrika()

  useEffect(() => {
    if (!selectedPayMethod) {
      setSelectedPayMethod(payMethodRadioOptions[0])
    }
  }, [payMethodRadioOptions, selectedPayMethod])

  const isAcquiringMethodSelected = selectedPayMethod?.id !== PayMethods.Qr
  const isQRMethodSelected = selectedPayMethod?.id === PayMethods.Qr

  const { makePayment, isPaymentPending } = useBillsFinancePay({
    invoice: balanceInvoice,
    invoiceIds,
    receiptMethod: receiptMethodsOptions[0].key,
    opened
  })

  const handleProceedPay = (formData: AcquiringFormData) => {
    makePayment({
      ...formData,
      cardId: selectedPayMethod?.number && selectedPayMethod?.id
    }).catch(() => setIsPayRequestRejected(true))
    reachGoal(YandexMetrikaEvent.INVOICE_PAYMENT)
  }

  const getTitle = () => {
    if (isPaymentMethodsLoading) {
      return (
        <div className="flex items-center gap-12">
          {setInvoiceListDrawerOpened && (
            <ButtonSkeleton className="h-40 w-40 rounded-full sm:h-[28px] sm:w-[28px]" />
          )}
          <ButtonSkeleton className="h-[44px] w-[148px] sm:h-[28px] sm:w-[82px]" />
        </div>
      )
    }

    if (isPaymentMethodsError) {
      return MODAL_TITLE_ERROR
    }
    if (isPayRequestRejected) {
      return 'Не удалось оплатить счёт'
    }

    return 'Оплата счёта'
  }

  const handleCloseDrawer = () => {
    setInvoiceListDrawerOpened?.(true)
    onClose()
  }

  return (
    <AppDrawer
      theme="white"
      title={
        <div className="flex items-center gap-24 lg:gap-12 sm:gap-8">
          {setInvoiceListDrawerOpened && (
            <GoBackButton onClick={handleCloseDrawer} />
          )}
          {getTitle()}
        </div>
      }
      opened={opened || false}
      onClose={onClose}
      contentWrapperClassName="px-0 lg:px-0"
    >
      {isPaymentMethodsLoading && !isError && (
        <div className="flex h-full flex-col px-40 pt-16 lg:px-22 sm:px-0">
          <div>
            <CardSkeleton height={isMobileScreen ? 108 : 100} />
            <ButtonSkeleton className="mt-40 h-[28px] w-[367px] sm:w-[269px]" />
            <div className="mt-16 flex w-full gap-12">
              <CardSkeleton
                className="flex-1"
                height={isMobileScreen ? 100 : 128}
              />
              <CardSkeleton
                className="flex-1"
                height={isMobileScreen ? 100 : 128}
              />
            </div>
            <ButtonSkeleton className="mt-40 h-[28px] w-[367px] sm:w-[269px]" />
            <CardSkeleton className="mt-8" height={isMobileScreen ? 108 : 56} />
            <ButtonSkeleton className="mt-20 h-[16px] w-full" />
            <ButtonSkeleton className="mt-8 h-[16px] w-[247px] sm:w-[318px]" />
            {isMobileScreen && (
              <>
                <ButtonSkeleton className="mt-8 h-[16px] w-full" />
                <ButtonSkeleton className="mt-8 h-[16px] w-[247px]" />
              </>
            )}
          </div>
          <ButtonSkeleton className="mb-40 mt-auto h-[56px] w-full sm:mb-20 sm:h-[44px]" />
        </div>
      )}
      {!isPaymentMethodsLoading && isError && (
        <div className="flex h-full flex-col px-40 lg:px-22 sm:px-0">
          <div className="mt-[22%]">
            <ErrorCard customDescription="Попробуйте повторить попытку" />
          </div>

          <AppButton
            fluid
            size={isMobileScreen ? 'small' : 'large'}
            onClick={onClose}
            className="mb-[40px] mt-auto pt-8"
            variant="secondary"
          >
            Понятно
          </AppButton>
        </div>
      )}
      {!isPaymentMethodsLoading && !isError && (
        <>
          <div className="flex-grow overflow-y-auto overflow-x-hidden sm:-mx-16 sm:px-16">
            <BillsListItem
              className="mx-40 lg:mx-24 sm:mx-0"
              unpaid
              invoice={invoice}
              variant="secondary"
            />

            <div>
              <p className="mb-16 ml-40 mt-32 font-pro font-bold text-24 lg:ml-24 md:text-20 sm:!mt-20 sm:ml-0 xxs:mx-0">
                Способ оплаты
              </p>

              <CardsGroup
                cardsQuantity={paymentMethods?.acquiring?.cards.length || 0}
                options={payMethodRadioOptions}
                selectedCard={selectedPayMethod}
                onCardSelect={setSelectedPayMethod}
              />
            </div>

            <div className="mx-40 lg:mx-24 sm:mx-0">
              {isAcquiringMethodSelected && (
                <FormProvider {...acquiringForm}>
                  <AcquiringPayForm isCommonPayment={isCommonPayment} />
                </FormProvider>
              )}

              <BillsPayInformation
                invoiceId={balanceInvoice.id || ''}
                isQRMethodSelected={isQRMethodSelected}
                urlDesc={paymentMethods?.acquiring?.urlDesc}
                urlOffer={paymentMethods?.acquiring?.urlOffer}
                className="pb-32 sm:pb-24"
              />
            </div>
          </div>

          {!isQRMethodSelected && (
            <BillsPayFooterWrapper>
              <BillsPayFooterContent
                debtSum={balanceInvoice.debtSum}
                buttonText="Оплатить"
                buttonLoading={isPaymentPending}
                isCommonPayment={isCommonPayment}
                onButtonClick={acquiringForm.handleSubmit(handleProceedPay)}
              />
            </BillsPayFooterWrapper>
          )}
        </>
      )}
    </AppDrawer>
  )
}

export default BillsPayDrawer
